import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, Bell, Settings as SettingsIcon, Plus, Activity, ChevronLeft, ChevronRight, RefreshCw, Sun, Moon, Sparkles, X, Target, TrendingUp, ArrowUpDown, Eraser, Globe2, LogOut, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import TopTicker from "./TopTicker";
import AssetWatchlist from "./AssetWatchlist";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Layout({
  children, onAddSetup, onStatusUpdate, sidebarOpen, onSidebarToggle,
  theme = "dark", onToggleTheme,
  // Watchlist props
  setups = [], allSetups = [], selectedId, onSelect,
  filter = "", onFilterChange,
  category = "ALLE", onCategoryChange, categories = [],
  onlyAttractive = false, onToggleAttractive,
  attractiveCount = 0, polling = false, onPoll,
  sort = "name_asc", onSortChange,
  onDeduplicateDone,
  backendError = false, onRetryBackend,
  watchlistLoading = false,
}) {
  const { user, logout } = useAuth();
  const [deduping, setDeduping] = useState(false);
  const handleDeduplicate = async () => {
    const dupes = await api.findDuplicates();
    if (dupes.total_duplicates === 0) { toast.info("Keine Duplikate gefunden."); return; }
    if (!window.confirm(`${dupes.total_duplicates} Duplikat(e) gefunden in ${dupes.groups.length} Gruppe(n). Bereinigen und jeweils die aktuellste Version behalten?`)) return;
    setDeduping(true);
    try {
      const r = await api.deduplicateSetups();
      toast.success(`${r.deleted} Duplikat(e) gelöscht.`);
      onDeduplicateDone?.();
    } catch { toast.error("Bereinigung fehlgeschlagen."); }
    finally { setDeduping(false); }
  };

  const location = useLocation();
  const isDashboard = location.pathname === "/";

  const navItem = (to, icon, label, testId) => {
    const Icon = icon;
    const active = location.pathname === to || (to !== "/" && location.pathname.startsWith(to));
    return (
      <NavLink
        to={to}
        data-testid={testId}
        title={label}
        className={`flex items-center gap-3 px-3 py-2 rounded-sm transition-colors ${
          active ? "bg-zinc-800 text-white" : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
        }`}
      >
        <Icon size={16} strokeWidth={1.75} className="shrink-0" />
        {sidebarOpen && <span className="font-display font-medium text-sm">{label}</span>}
      </NavLink>
    );
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Nav items */}
      <div className="p-2 space-y-1 shrink-0">
        {navItem("/", LayoutDashboard, "Dashboard", "nav-dashboard")}
        {navItem("/makro", Globe2, "Makro", "nav-makro")}
        {user?.is_admin && navItem("/admin", ShieldCheck, "Admin", "nav-admin")}
        {navItem("/alerts", Bell, "Alerts", "nav-alerts")}
        {navItem("/settings", SettingsIcon, "Einstellungen", "nav-settings")}
      </div>

      {/* Watchlist — only on Dashboard and when sidebar is open */}
      {sidebarOpen && isDashboard && (
        <div className="flex flex-col flex-1 min-h-0 border-t border-zinc-800 mt-1">
          {/* Watchlist header */}
          <div className="p-3 border-b border-zinc-800 shrink-0 bg-zinc-950">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-display font-bold text-sm tracking-tight">Watchlist</div>
                <div className="text-[10px] text-zinc-500 font-mono-num">{setups.length} / {allSetups.length} Setups</div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={onPoll}
                disabled={polling || allSetups.length === 0}
                className="h-7 px-2 border-zinc-700 bg-transparent hover:bg-zinc-800 text-[10px]"
                data-testid="btn-poll-now"
                title="Preise aktualisieren"
              >
                <RefreshCw size={12} className={polling ? "animate-spin" : ""} />
              </Button>
            </div>
            <Input
              placeholder="Suche..."
              value={filter}
              onChange={(e) => onFilterChange(e.target.value)}
              className="bg-zinc-900 border-zinc-800 h-8 text-xs"
              data-testid="search-setups"
            />
            <button
              onClick={onToggleAttractive}
              data-testid="toggle-only-attractive"
              className={`mt-2 w-full px-2 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider rounded-sm flex items-center justify-center gap-1.5 transition-colors border ${
                onlyAttractive
                  ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300"
                  : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:bg-zinc-800"
              }`}
            >
              <Sparkles size={11} className={onlyAttractive ? "text-emerald-400" : ""} />
              Nur attraktiv ({attractiveCount})
            </button>
            <div className="flex flex-wrap gap-1 mt-2">
              {categories.map(c => (
                <button
                  key={c}
                  onClick={() => onCategoryChange(c)}
                  data-testid={`cat-filter-${c}`}
                  className={`px-2 py-0.5 text-[10px] font-display font-bold uppercase tracking-wider rounded-sm ${
                    category === c ? "bg-white text-black" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            {/* Sort controls */}
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-zinc-600 font-display">
                <ArrowUpDown size={9} /> Sortierung
              </div>
              <div className="grid grid-cols-3 gap-1">
                {[
                  { key: "name_asc",    label: "A→Z" },
                  { key: "name_desc",   label: "Z→A" },
                  { key: "change_desc", label: "% ↑" },
                  { key: "change_asc",  label: "% ↓" },
                  { key: "price_desc",  label: "Kurs ↑" },
                  { key: "price_asc",   label: "Kurs ↓" },
                ].map(o => (
                  <button
                    key={o.key}
                    onClick={() => onSortChange?.(o.key)}
                    className={`py-1 text-[10px] font-display font-bold uppercase tracking-wider rounded-sm text-center ${
                      sort === o.key ? "bg-white text-black" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Deduplicate button */}
            <button
              onClick={handleDeduplicate}
              disabled={deduping}
              className="mt-2 w-full px-2 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider rounded-sm flex items-center justify-center gap-1.5 transition-colors border bg-zinc-900 border-zinc-800 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-300 disabled:opacity-50"
              title="Doppelte Setups erkennen und bereinigen"
            >
              <Eraser size={11} /> Duplikate bereinigen
            </button>
          </div>
          {/* Scrollable list */}
          <div className="flex-1 overflow-y-auto">
            {watchlistLoading ? (
              <WatchlistSkeleton />
            ) : (
              <AssetWatchlist setups={setups} selectedId={selectedId} onSelect={onSelect} />
            )}
          </div>
          {/* Quick stats */}
          <div className="shrink-0 border-t border-zinc-800 p-3 space-y-1.5">
            <div className="text-[9px] uppercase tracking-widest text-zinc-600 font-display mb-2">Übersicht</div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="flex items-center gap-1.5 text-zinc-500"><Target size={10} /> In / nahe Zone</span>
              <span className="font-mono-num font-bold text-emerald-400">{attractiveCount}</span>
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="flex items-center gap-1.5 text-zinc-500"><Activity size={10} /> Aktiv</span>
              <span className="font-mono-num font-bold text-zinc-300">{allSetups.filter(s => s.status !== "closed").length}</span>
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="flex items-center gap-1.5 text-zinc-500"><TrendingUp size={10} /> Gesamt</span>
              <span className="font-mono-num font-bold text-zinc-400">{allSetups.length}</span>
            </div>
          </div>
        </div>
      )}

      {/* Bottom: powered by (only when open, no watchlist) */}
      {sidebarOpen && !isDashboard && (
        <div className="mt-auto p-3 pt-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-display px-3 mb-1">Friedrich·Trade</div>
          <div className="text-[10px] text-zinc-500 font-mono-num px-3">Allocation Hub v1.0</div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-app text-app flex flex-col">
      {/* Top bar */}
      <header className="h-14 border-b border-zinc-800 px-4 flex items-center justify-between bg-app sticky top-0 z-30 gap-4">
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={onSidebarToggle}
            className="p-1.5 hover:bg-zinc-800 rounded-sm transition-colors"
            data-testid="btn-sidebar-toggle"
            title={sidebarOpen ? "Sidebar einklappen" : "Sidebar ausklappen"}
          >
            {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-white text-black flex items-center justify-center font-display font-black text-[11px] tracking-tighter">FT</div>
            <div className="font-display font-bold tracking-tight text-sm sm:text-base whitespace-nowrap">
              FRIEDRICH<span className="text-zinc-500 hidden sm:inline">·TRADE</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2 text-xs text-zinc-500 ml-2">
            <Activity size={12} className="text-emerald-500" />
            <span className="font-mono-num">LIVE</span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {user && (
            <div className="hidden sm:flex items-center gap-1.5 text-xs text-zinc-500 mr-1">
              <span className="font-mono-num truncate max-w-[120px]">{user.name || user.email}</span>
              <button
                onClick={logout}
                title="Abmelden"
                className="p-1 hover:bg-zinc-800 rounded-sm transition-colors text-zinc-500 hover:text-zinc-300"
              >
                <LogOut size={13} />
              </button>
            </div>
          )}
          <button
            onClick={onToggleTheme}
            data-testid="btn-theme-toggle"
            title={theme === "dark" ? "Light Mode" : "Dark Mode"}
            className="h-9 w-9 flex items-center justify-center border border-zinc-700 rounded-sm hover:bg-zinc-800 transition-colors text-zinc-300"
          >
            {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
          </button>
          <Button
            data-testid="header-status-update-btn"
            onClick={onStatusUpdate}
            variant="outline"
            className="border-emerald-600/40 bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-400 rounded-sm font-display font-bold tracking-wide text-xs uppercase h-9 px-3"
          >
            <RefreshCw size={14} strokeWidth={2.5} className="mr-1" />
            <span className="hidden sm:inline">Status Update</span>
            <span className="sm:hidden">Status</span>
          </Button>
          <Button
            data-testid="header-add-setup-btn"
            onClick={onAddSetup}
            className="bg-white text-black hover:bg-zinc-200 rounded-sm font-display font-bold tracking-wide text-xs uppercase h-9 px-4"
          >
            <Plus size={14} strokeWidth={2.5} className="mr-1" />
            Setup
          </Button>
        </div>
      </header>

      {/* Backend error banner */}
      {backendError && (
        <div className="bg-rose-950/80 border-b border-rose-900 px-4 py-2 flex items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-2 text-rose-300">
            <span className="font-bold">⚠</span>
            <span>Backend nicht erreichbar — Daten werden nicht aktualisiert.</span>
          </div>
          <button
            onClick={onRetryBackend}
            className="text-rose-300 hover:text-rose-100 border border-rose-800 px-3 py-1 rounded-sm font-display uppercase tracking-wider transition-colors"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {/* Live ticker bar */}
      <TopTicker />

      <div className="flex-1 flex relative">
        {/* Mobile overlay backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/60 z-20 lg:hidden"
            onClick={onSidebarToggle}
          />
        )}

        {/* Sidebar — mobile: fixed overlay, desktop: static */}
        <aside
          className={`
            border-r border-zinc-800 bg-zinc-950 flex flex-col shrink-0 transition-all duration-200
            lg:static lg:translate-x-0
            fixed top-[5.75rem] left-0 bottom-0 z-20
            ${sidebarOpen
              ? "translate-x-0 w-[82vw] sm:w-80"
              : "-translate-x-full lg:translate-x-0 w-[82vw] sm:w-80 lg:w-14"
            }
          `}
        >
          {/* Mobile close button */}
          {sidebarOpen && (
            <button
              onClick={onSidebarToggle}
              className="lg:hidden absolute top-2 right-2 p-1 hover:bg-zinc-800 rounded-sm text-zinc-400"
            >
              <X size={14} />
            </button>
          )}
          {sidebarContent}
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
}

function WatchlistSkeleton() {
  return (
    <div className="divide-y divide-zinc-900 animate-pulse" data-testid="watchlist-skeleton">
      {[...Array(7)].map((_, i) => (
        <div key={i} className="px-3 py-2.5">
          <div className="flex items-center justify-between mb-1.5">
            <div className="h-3.5 bg-zinc-800 rounded" style={{ width: `${55 + (i % 3) * 15}px` }} />
            <div className="h-3.5 bg-zinc-800 rounded w-14" />
          </div>
          <div className="flex items-center justify-between">
            <div className="h-2.5 bg-zinc-800/50 rounded w-16" />
            <div className="h-2.5 bg-zinc-800/50 rounded w-10" />
          </div>
        </div>
      ))}
    </div>
  );
}
